// The hands-free practice loop. Per attempt:
//   get ready  → wait until the rider is lying down (or a timer)
//   armed      → start recording, "ready" tone, short (optionally random) delay
//   capturing  → "go" tone; analyse every frame until the landing is held
//   review     → speak one observation, save the attempt, then reset
// The rider never needs to touch the phone.

import { analyzeAttempt, type AnalysisContext } from '../core/analysis';
import { buildFeedback, type Feedback } from '../core/feedback';
import { observeFrame } from '../core/features';
import { paramsFor, type AnalysisParams } from '../core/params';
import { serializeFrames, type PoseFrame } from '../core/pose';
import { sessionStats, type TruthLabel } from '../core/stats';
import { cameraMoved, checkAnchors, grayFrame, personBox, refreshAnchors } from './anchors';
import { speaker } from './audio';
import { db, deleteSession, pruneClips, uid, type Anchor, type AttemptRecord, type CalibrationRecord, type SessionRecord, type Settings, type Still } from './db';
import { ClipRecorder, canvasToBlob } from './recorder';
import type { FrameSource, PoseSample } from './source';
import { keepAwake } from './wakelock';

export type Phase = 'getReady' | 'armed' | 'capturing' | 'review' | 'paused' | 'cameraMoved' | 'done';

export interface Scenario {
  key: string;
  instruction: string;
  label: TruthLabel;
}

export const EXPERIMENT_SCENARIOS: Scenario[] = [
  { key: 'normal', instruction: 'Normal pop-up. Aim for your marks.', label: 'clean' },
  { key: 'frontShort', instruction: 'Land with your front foot short of its mark.', label: 'frontOff' },
  { key: 'normal', instruction: 'Normal pop-up. Aim for your marks.', label: 'clean' },
  { key: 'rearOff', instruction: 'Land with your back foot off the centre line.', label: 'rearOff' },
  { key: 'shuffle', instruction: 'Land short, then step your front foot onto its mark.', label: 'stepped' },
  { key: 'normal', instruction: 'Normal pop-up. Aim for your marks.', label: 'clean' },
  { key: 'narrow', instruction: 'Land with your feet too close together.', label: 'bothOff' },
  { key: 'frontLong', instruction: 'Land with your front foot past its mark.', label: 'frontOff' },
  { key: 'wide', instruction: 'Land with your feet wider than your marks.', label: 'bothOff' },
  { key: 'shuffle', instruction: 'Land short, then step your front foot onto its mark.', label: 'stepped' },
];

export interface PracticeState {
  phase: Phase;
  /** Large status line, readable from a distance. */
  headline: string;
  detail: string;
  /** Counted attempts so far (no-pop-up captures aren't counted). */
  count: number;
  target: number;
  personVisible: boolean;
  prone: boolean;
  lastAttempt: AttemptRecord | null;
  lastFeedback: Feedback | null;
  attempts: AttemptRecord[];
  scenario: Scenario | null;
  sessionId: string | null;
  /** Why the set is paused (null while running). */
  pauseReason: 'user' | 'hidden' | 'noFrames' | 'nobody' | null;
  /** Live analysis is running below ~20 frames per second. */
  slow: boolean;
}

export interface PracticeOptions {
  source: FrameSource;
  calibration: CalibrationRecord;
  settings: Settings;
  mode: 'practice' | 'experiment';
}

const PRONE_RATIO = 0.45;
const PRONE_HOLD_MS = 1000;
const READY_TIMEOUT_MS = 15000;
const TIMER_READY_MS = 6000;

export class PracticeController {
  private state: PracticeState;
  private listeners = new Set<() => void>();
  private readonly params: AnalysisParams;
  private source: FrameSource;
  private recorder: ClipRecorder;
  private anchors: Anchor[];
  private session: SessionRecord | null = null;
  private saving: Promise<void> | null = null;
  private lastFrameAt = 0;
  private watchdog: ReturnType<typeof setInterval> | null = null;

  private phaseStart = 0;
  private proneSince: number | null = null;
  private frames: PoseFrame[] = [];
  private t0 = 0;
  private videoOffset = 0;
  private cueAt: number | null = null;
  private goAt = 0;
  private stills: { landing: (Still & { pending?: Promise<void> }) | null; settled: Still | null } = { landing: null, settled: null };
  private finishing = false;
  private lastAnchorCheck = 0;
  private anchorStrikes = 0;
  private misses = 0;
  private rebaselineAnchors = false;
  private latestSample: PoseSample | null = null;
  private proneMisses = 0;
  private waitingFallback = false;
  private scenarioIndex = 0;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private destroyed = false;
  /** Lets the demo rider react to the loop's cues. */
  onCue: ((cue: 'getReady' | 'go' | 'armedSilent' | 'review') => void) | null = null;

  constructor(private readonly opts: PracticeOptions) {
    const { settings, calibration } = opts;
    this.params = paramsFor(
      { strictness: settings.strictness, holdMs: settings.holdMs, checkAngle: settings.checkAngle },
      calibration.reference.noise,
    );
    this.source = opts.source;
    this.recorder = new ClipRecorder(opts.source.stream);
    this.anchors = opts.calibration.anchors;
    this.state = {
      phase: 'getReady',
      headline: 'Starting…',
      detail: '',
      count: 0,
      target: settings.attemptsPerSet,
      personVisible: false,
      prone: false,
      lastAttempt: null,
      lastFeedback: null,
      attempts: [],
      scenario: null,
      sessionId: null,
      pauseReason: null,
      slow: false,
    };
  }

  // --- store interface (useSyncExternalStore) --------------------------------
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getState = () => this.state;
  private set(patch: Partial<PracticeState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  get analysisParams() {
    return this.params;
  }

  /** The most recent analysed frame (for drawing the live overlay). */
  get latest(): PoseSample | null {
    return this.latestSample;
  }

  private ctx(cueTime?: number): AnalysisContext {
    const c = this.opts.calibration;
    return { mapping: c.mapping, reference: c.reference, params: this.params, stance: c.stance, cueTime };
  }

  // --- lifecycle --------------------------------------------------------------

  /** Call from a tap handler (unlocks audio on iOS). */
  async begin(): Promise<void> {
    speaker.unlock();
    keepAwake(true);
    const { settings, calibration, source, mode } = this.opts;
    this.session = {
      id: uid(),
      calibrationId: calibration.id,
      startedAt: Date.now(),
      endedAt: null,
      mode,
      source: source.kind,
      params: this.params,
      model: settings.model,
      cueMode: settings.cueMode,
      attemptIds: [],
    };
    await db.putSession(this.session);
    this.set({ sessionId: this.session.id });
    this.lastFrameAt = performance.now();
    source.start((s) => this.onFrame(s));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.watchdog = setInterval(this.checkHealth, 1000);
    this.enterGetReady(true);
  }

  /** Switches to a re-opened camera (e.g. after iOS killed the old track). */
  setSource(source: FrameSource): void {
    this.source.stop();
    this.recorder.discard();
    this.source = source;
    this.recorder = new ClipRecorder(source.stream);
    this.lastFrameAt = performance.now();
    if (!this.destroyed && this.session) source.start((s) => this.onFrame(s));
  }

  get currentSource(): FrameSource {
    return this.source;
  }

  private onVisibility = () => {
    const p = this.state.phase;
    if (document.visibilityState === 'hidden' && p !== 'done' && p !== 'paused' && p !== 'cameraMoved') this.pause('hidden');
  };

  private isActive() {
    const p = this.state.phase;
    return p === 'getReady' || p === 'armed' || p === 'capturing';
  }

  /** Pauses if frames stop arriving (camera interrupted, tab throttled). */
  private checkHealth = () => {
    if (this.destroyed) return;
    const now = performance.now();
    if (this.isActive() && now - this.lastFrameAt > 2500) {
      this.pause('noFrames');
      void speaker.say('I’ve lost the camera picture. Paused.', { interrupt: true });
    }
    const fps = this.source.stats().fps;
    const slow = fps > 0 && fps < 20 && now - this.lastFrameAt < 1000;
    if (slow !== this.state.slow) this.set({ slow });
  };

  pause(reason: NonNullable<PracticeState['pauseReason']> = 'user'): void {
    if (this.state.phase === 'done' || this.state.phase === 'paused') return;
    this.abortCapture();
    this.clearTimers();
    if (reason === 'user') speaker.stopSpeaking();
    const detail = {
      user: 'Tap resume when you’re ready.',
      hidden: 'Paused while the app was in the background.',
      noFrames: 'The camera picture stopped. Resume to restart it.',
      nobody: 'I haven’t seen a pop-up for a while. Resume when you’re ready.',
    }[reason];
    this.set({ phase: 'paused', headline: 'Paused', detail, pauseReason: reason });
  }

  resume(): void {
    const from = this.state.phase;
    if (from !== 'paused' && from !== 'cameraMoved') return;
    speaker.unlock();
    keepAwake(true);
    this.anchorStrikes = 0;
    this.misses = 0;
    this.lastFrameAt = performance.now();
    // "Continue anyway" after a camera-moved warning: accept the new view.
    if (from === 'cameraMoved') this.rebaselineAnchors = true;
    this.set({ pauseReason: null });
    this.enterGetReady(false);
  }

  /** Ends the set early. Returns the session id if any attempts were saved. */
  async finish(): Promise<string | null> {
    this.abortCapture();
    this.clearTimers();
    if (this.saving) await this.saving.catch(() => {});
    return this.complete(false);
  }

  destroy(): void {
    this.destroyed = true;
    this.abortCapture();
    this.clearTimers();
    if (this.watchdog) clearInterval(this.watchdog);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.source.stop();
    speaker.stopSpeaking();
    keepAwake(false);
  }

  private clearTimers() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  private later(ms: number, fn: () => void) {
    this.timers.push(setTimeout(() => !this.destroyed && fn(), ms));
  }

  private abortCapture() {
    this.recorder.discard();
    this.frames = [];
    this.cueAt = null;
    this.stills = { landing: null, settled: null };
  }

  // --- phases -------------------------------------------------------------------

  private enterGetReady(first: boolean) {
    this.abortCapture();
    this.proneSince = null;
    this.phaseStart = performance.now();
    let scenario: Scenario | null = null;
    if (this.opts.mode === 'experiment') {
      scenario = EXPERIMENT_SCENARIOS[this.scenarioIndex % EXPERIMENT_SCENARIOS.length];
    }
    const { cueMode } = this.opts.settings;
    this.set({
      phase: 'getReady',
      headline: cueMode === 'timer' ? 'Get ready' : 'Lie down',
      detail: scenario ? scenario.instruction : cueMode === 'timer' ? 'Next attempt starts shortly.' : 'Lie on the board when you’re ready.',
      scenario,
    });
    const line = first ? (cueMode === 'timer' ? 'Get ready. Lie down on the board.' : 'Lie down on the board when you’re ready.') : 'Reset.';
    void speaker.say(scenario ? `${line} ${scenario.instruction}` : line);
    this.onCue?.('getReady');
  }

  private async enterArmed() {
    const self = this.opts.settings.cueMode === 'none';
    const task = this.state.scenario?.instruction;
    this.set({ phase: 'armed', headline: self ? 'Go when ready' : 'Ready…', detail: task ?? (self ? 'Pop up whenever you like.' : 'Wait for the beep.') });
    this.phaseStart = performance.now();
    // The attempt's clock starts now; the clip starts whenever the recorder
    // actually begins, and that offset is kept for aligning the replay.
    this.frames = [];
    this.t0 = performance.now();
    this.videoOffset = 0;
    try {
      this.videoOffset = (await this.recorder.start()) - this.t0;
    } catch (e) {
      console.warn('recording unavailable', e);
    }
    if (this.state.phase !== 'armed' || this.destroyed) return;
    if (self) {
      this.cueAt = null;
      this.goAt = performance.now();
      this.set({ phase: 'capturing', headline: 'Watching…', detail: '' });
      this.onCue?.('armedSilent');
      return;
    }
    speaker.tone('ready');
    const delay = this.opts.settings.randomDelay ? 1100 + Math.random() * 1600 : 1500;
    this.later(delay, () => {
      if (this.state.phase !== 'armed') return;
      speaker.tone('go');
      this.goAt = performance.now();
      this.cueAt = this.goAt - this.t0;
      this.set({ phase: 'capturing', headline: 'Go!', detail: '' });
      this.onCue?.('go');
    });
  }

  // --- per-frame loop -----------------------------------------------------------

  private onFrame(s: PoseSample) {
    if (this.destroyed) return;
    this.lastFrameAt = performance.now();
    const { calibration } = this.opts;
    const obs = s.lm ? observeFrame({ t: s.t, lm: s.lm }, calibration.mapping, { minVisibility: 0.3 }) : null;
    const ratio = obs?.height != null && calibration.reference.standHeight > 0 ? obs.height / calibration.reference.standHeight : null;
    const prone = ratio !== null && ratio < PRONE_RATIO;
    const visible = !!s.lm;
    this.latestSample = s; // read by the overlay's animation loop, not via React
    if (visible !== this.state.personVisible || prone !== this.state.prone) this.set({ personVisible: visible, prone });

    switch (this.state.phase) {
      case 'getReady':
        return this.tickGetReady(s, prone);
      case 'armed':
        this.frames.push({ t: s.t - this.t0, lm: s.lm });
        // Standing up before the beep is a false start.
        if (this.opts.settings.cueMode !== 'none' && visible && !prone && ratio !== null && ratio > 0.7 && s.t - this.phaseStart > 400) {
          if (this.opts.settings.cueMode === 'auto') {
            void speaker.say('Wait for the beep.', { interrupt: true });
            this.clearTimers();
            this.enterGetReady(false);
          }
        }
        return;
      case 'capturing':
        this.frames.push({ t: s.t - this.t0, lm: s.lm });
        return this.tickCapturing(s);
      default:
        return;
    }
  }

  private tickGetReady(s: PoseSample, prone: boolean) {
    const now = s.t;
    const { cueMode } = this.opts.settings;
    this.maybeCheckCamera(s);
    if (this.state.phase !== 'getReady') return;
    const elapsed = now - this.phaseStart;
    if (elapsed < 1200) return; // let the rider hear the instruction
    if (prone) this.proneSince ??= now;
    else this.proneSince = null;
    const proneReady = this.proneSince !== null && now - this.proneSince >= PRONE_HOLD_MS;
    if (proneReady) this.proneMisses = 0;
    // Timer mode — or automatic mode that has learned it can't see the rider
    // lying down (pose models are weakest face-down) — keeps a fixed rhythm.
    if (cueMode === 'timer' || (this.proneMisses > 0 && cueMode === 'auto')) {
      if (proneReady || elapsed >= TIMER_READY_MS) void this.enterArmed();
      return;
    }
    if (proneReady) {
      void this.enterArmed();
    } else if (elapsed > READY_TIMEOUT_MS && !this.waitingFallback) {
      this.proneMisses++;
      this.waitingFallback = true;
      void speaker.say(
        cueMode === 'auto'
          ? 'I can’t tell when you’re lying down, so I’ll start each attempt on a timer.'
          : 'I can’t tell if you’re lying down. Starting anyway.',
      );
      this.later(3500, () => {
        this.waitingFallback = false;
        if (this.state.phase === 'getReady') void this.enterArmed();
      });
    }
  }

  private tickCapturing(s: PoseSample) {
    if (this.finishing) return;
    const a = analyzeAttempt(this.frames, this.ctx(this.cueAt ?? undefined));
    const since = s.t - this.goAt;
    if (a.events.landing !== undefined && !this.stills.landing) {
      this.stills.landing = this.grabStill(s);
    }
    if (a.holdComplete) {
      this.stills.settled = this.grabStill(s);
      speaker.tone('hold');
      void this.finishAttempt();
      return;
    }
    const started = a.events.onset !== undefined || a.events.arrival !== undefined;
    const selfStart = this.cueAt === null;
    if (!started && since > (selfStart ? 25000 : 5000)) {
      void this.finishAttempt();
    } else if (since > (selfStart ? 30000 : 10000)) {
      void this.finishAttempt();
    }
  }

  private grabStill(s: PoseSample): Still & { pending?: Promise<void> } {
    const still: Still & { pending?: Promise<void> } = { key: '', t: s.t - this.t0, lm: s.lm };
    const canvas = this.source.snapshot(960);
    still.pending = canvasToBlob(canvas)
      .then((blob) => db.putBlob(blob))
      .then((key) => void (still.key = key))
      .catch(() => {});
    return still;
  }

  private maybeCheckCamera(s: PoseSample) {
    if (!this.anchors.length || s.t - this.lastAnchorCheck < 2500) return;
    this.lastAnchorCheck = s.t;
    const src = this.source;
    const f = grayFrame(src.view, src.width(), src.height());
    const box = personBox(s.lm);
    if (this.rebaselineAnchors) {
      this.anchors = refreshAnchors(f, this.anchors, box ? [box] : []);
      this.rebaselineAnchors = false;
      return;
    }
    // Without a detected person we don't know what the rider covers (a prone
    // body often goes undetected), so only scenery anchors are trusted.
    const usable = box ? this.anchors : this.anchors.filter((a) => a.kind === 'scene');
    const res = checkAnchors(f, usable, box ? [box] : []);
    this.anchorStrikes = cameraMoved(res) ? this.anchorStrikes + 1 : 0;
    if (this.anchorStrikes >= (box ? 2 : 3)) {
      speaker.tone('alert');
      void speaker.say('The camera seems to have moved. Please recalibrate.', { interrupt: true });
      this.set({ phase: 'cameraMoved', headline: 'Camera moved', detail: 'The floor marks no longer line up. Recalibrate to keep measurements honest.' });
    }
  }

  // --- end of an attempt ---------------------------------------------------------

  private finishAttempt(): Promise<void> {
    if (this.finishing) return this.saving ?? Promise.resolve();
    this.finishing = true;
    this.saving = this.finishAttemptInner().finally(() => {
      this.finishing = false;
      this.saving = null;
    });
    return this.saving;
  }

  private async finishAttemptInner() {
    const frames = this.frames;
    const cueTime = this.cueAt;
    const videoOffsetMs = this.videoOffset;
    const stills = this.stills;
    this.frames = [];
    this.set({ phase: 'review', headline: 'Analysing…', detail: '' });
    this.onCue?.('review');
    try {
      const clip = await this.recorder.stop();
      const analysis = analyzeAttempt(frames, this.ctx(cueTime ?? undefined));
      // Nobody (or almost nobody) in view isn't an attempt either.
      const nobody =
        analysis.issues.includes('noPose') ||
        (analysis.outcome === 'unassessed' && analysis.events.landing === undefined && analysis.coverage < 0.3);
      if (analysis.outcome === 'none' || nobody) {
        this.misses++;
        await Promise.all([stills.landing?.pending, (stills.settled as { pending?: Promise<void> } | null)?.pending]);
        for (const k of [stills.landing?.key, stills.settled?.key]) if (k) await db.deleteBlob(k);
        if (this.misses >= 3) {
          speaker.tone('alert');
          void speaker.say('I haven’t seen a pop-up for a while. Pausing. Check the camera can see you, then tap resume.', { interrupt: true });
          if (this.state.phase === 'review') this.pause('nobody');
          return;
        }
        await speaker.say(nobody ? 'I couldn’t see you. Let’s go again.' : 'No pop-up detected. Let’s go again.', { interrupt: true });
        this.set({ headline: nobody ? 'Couldn’t see you' : 'No pop-up detected', detail: '' });
        this.later(800, () => this.state.phase === 'review' && this.enterGetReady(false));
        return;
      }
      this.misses = 0;
      const feedback = buildFeedback(analysis, this.params, { announceTime: this.opts.settings.announceTime });
      speaker.tone(analysis.outcome === 'clean' ? 'clean' : analysis.outcome === 'adjust' ? 'adjust' : 'unassessed');
      this.set({ headline: feedback.headline, detail: feedback.spoken, lastFeedback: feedback });
      const isLast = this.state.target > 0 && this.state.count + 1 >= this.state.target;
      const speech = speaker.say(feedback.spoken);

      await Promise.all([stills.landing?.pending, (stills.settled as { pending?: Promise<void> } | null)?.pending]);
      const videoKey = clip ? await db.putBlob(clip.blob) : null;
      const record: AttemptRecord = {
        id: uid(),
        sessionId: this.session!.id,
        calibrationId: this.opts.calibration.id,
        index: this.state.count,
        createdAt: Date.now(),
        frames: serializeFrames(frames),
        width: this.source.width(),
        height: this.source.height(),
        cueTime,
        analysis,
        source: 'live',
        model: this.source.kind === 'demo' ? 'sim' : this.opts.settings.model,
        videoKey,
        videoMime: clip?.mime ?? null,
        videoOffsetMs,
        stills: {
          landing: stills.landing?.key ? { key: stills.landing.key, t: stills.landing.t, lm: stills.landing.lm } : null,
          settled: stills.settled?.key ? { key: stills.settled.key, t: stills.settled.t, lm: stills.settled.lm } : null,
        },
        scenario: this.state.scenario?.key ?? null,
        label: this.state.scenario?.label ?? null,
        note: '',
      };
      await db.putAttempt(record);
      this.session!.attemptIds.push(record.id);
      await db.putSession(this.session!);
      this.scenarioIndex++;
      this.set({ count: this.state.count + 1, lastAttempt: record, attempts: [...this.state.attempts, record] });
      await speech;
      if (this.destroyed || this.state.phase !== 'review') return;
      if (isLast) await this.complete(true);
      else this.later(900, () => this.state.phase === 'review' && this.enterGetReady(false));
    } catch (e) {
      // Storage full, a dropped database connection… keep the loop alive.
      console.error('saving the attempt failed', e);
      if (this.destroyed || this.state.phase !== 'review') return;
      void speaker.say('I couldn’t save that one.', { interrupt: true });
      this.later(1500, () => this.state.phase === 'review' && this.enterGetReady(false));
    }
  }

  private async complete(announce: boolean): Promise<string | null> {
    const session = this.session;
    this.source.stop();
    keepAwake(false);
    if (!session) return null;
    if (session.attemptIds.length === 0) {
      await deleteSession(session.id);
      this.set({ phase: 'done', headline: 'Set ended', detail: '' });
      return null;
    }
    session.endedAt = Date.now();
    await db.putSession(session);
    const stats = sessionStats(this.state.attempts.map((a) => a.analysis), this.params);
    if (announce) {
      const n = this.state.attempts.length;
      void speaker.say(`Set complete. ${stats.counts.clean} clean out of ${n}.`);
    }
    this.set({ phase: 'done', headline: 'Set complete', detail: '' });
    void pruneClips(this.opts.settings.keepClipSessions);
    return session.id;
  }
}
